<template>
  <div :class="panelClass">
    <component
      :is="headerComponent"
      v-if="showTopBanner"
      v-bind="headerProps"
      :title="_title"
      :buttons="topButtons"
      @button-click="handleButtonClick"
    />
    <div
      v-if="content"
      class="owal panel container"
    >
      <owal-form
        v-if="content.form"
        :form="content.form"
        :embedded="true"
        @change="inputChanged"
      />
      <component
        :is="content.component"
        v-else-if="content.component"
        class="owal panel component content"
        :embedded="true"
        @change="inputChanged"
      />
      <div
        v-else-if="content.text"
        class="owal panel text content"
      >
        {{ content.text }}
      </div>
    </div>
    <div
      v-else
      class="owal panel container"
    >
      <slot class="owal panel slot content" />
    </div>
    <component
      :is="footerComponent"
      v-if="bottomButtons"
      position="bottom"
      :buttons="bottomButtons"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script>
import OwalPanelHeader from './OwalPanelHeader'
import { OwalPanelType } from './OwalPanelType'
import OwalForm from '../forms/OwalForm.vue'

export default {
  name: 'OwalPanel',
  components: {
    OwalPanelHeader,
    OwalForm,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    showTopBanner: {
      type: Boolean,
      default: true,
    },
    headerComponent: {
      type: Object,
      default: () => OwalPanelHeader,
    },
    headerProps: {
      type: Object,
      default: () => {
        if (this) {
          return { title: this.title }
        }
        return { title: '' }
      },
    },
    footerComponent: {
      type: Object,
      default: () => OwalPanelHeader,
    },
    content: {
      type: Object,
      default: function () {
        return undefined
      },
      panelType: {
        type: Object,
        default: function () {
          return OwalPanelType.INPUT
        },
      },
      showCloseButton: {
        type: Boolean,
        default: false,
      },
    },
    active: {
      type: Boolean,
      default: true,
    },
    bottomButtons: {
      type: Array,
      default: undefined,
    },
    topButtons: {
      type: Array,
      default: undefined,
    },
  },
  data () {
    return {
      OwalPanelHeader,
    }
  },
  computed: {
    _title: function () {
      let title = this.title

      if (!title && this.headerProps && this.headerProps.title) {
        title = this.headerProps.title
      }

      /* If there's no explicit title, and the content is a form, use the form's title. */
      if (!title && this.content) {
        if (this.content.form) {
          title = this.content.form.name
        }
      }

      return title
    },
    panelClass: function () {
      if (this.active) {
        return 'owal panel active window'
      }
      return 'owal panel inactive window'
    },
  },
  methods: {
    inputChanged: function (submission) {
      this.$emit('change', submission)
    },
    handleButtonClick: function (identifier) {
      if (this.topButtons) {
        const topButton = this.topButtons.find(button => button.identifier === identifier)
        if (topButton && topButton.callback) {
          topButton.callback()
        }
      }

      if (this.bottomButtons) {
        const bottomButton = this.bottomButtons.find(button => button.identifier === identifier)
        if (bottomButton && bottomButton.callback) {
          bottomButton.callback()
        }
      }

      this.$emit('button-click', identifier)
    },
  },
}
</script>

<style scoped>
  @font-face {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 700;
    src: local('Lato Bold'), local('Lato-Bold'),
      url('https://fonts.gstatic.com/s/lato/v14/MgNNr5y1C_tIEuLEmicLmwLUuEpTyoUstqEm5AMlJo4.woff2')
        format('woff2');
    unicode-range: U+0-FF, U+131, U+152-153, U+2BB-2BC, U+2C6, U+2DA, U+2DC,
      U+2000-206F, U+2074, U+20AC, U+2122, U+2212, U+2215;
  }

  .owal.panel.active.window,
  .owal.panel.inactive.window {
    border-radius: 5px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .owal.panel.active.window {
    box-shadow: 0 0 0.625rem 0 rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(151, 151, 151, 1);
  }

  .owal.panel.active.window .owal.panel.container {
    opacity: 1;
  }

  .owal.panel.inactive.window {
    box-shadow: none;
    border: 1px solid rgba(151, 151, 151, 0.4);
    pointer-events: none;
    user-select: none;
  }

  .owal.panel.inactive.window::selection {
    background: none;
  }

  .owal.panel.inactive.window .owal.panel.container,
  .owal.panel.inactive.window .owal.panel.top.header,
  .owal.panel.inactive.window .owal.panel.middle.header {
    opacity: 0.4;
  }

  .owal.panel.container {
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0.9375rem;
  }

  .owal.panel.component.content {
    padding: 0;
    overflow: visible;
  }

  .owal.panel.active.window,
  .owal.panel.inactive.window,
  .owal.panel.container,
  .owal.panel.top.header,
  .owal.panel.middle.header {
    transition-property: opacity;
    transition-duration: 0.3s;
    transition-timing-function: ease-in;
  }

  @media only screen and (max-width: 768px) {
    /* Add mobile scrolling. */
    .owal.panel.container {
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
    }
  }
</style>
